import React from 'react';
import { useI18n } from '../../../../hooks/useI18n';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

interface TotalInfo {
  label: string;
  score: number;
  total: number;
  comment: string;
}

interface ScoreRadarProps {
  totalInfo: TotalInfo[];
  className?: string;
}

const ScoreRadar: React.FC<ScoreRadarProps> = ({ totalInfo, className = '' }) => {
  const { t } = useI18n();

  // 根据分数计算等级
  const getGrade = (score: number, total: number): string => {
    const percentage = (score / total) * 100;
    if (percentage >= 90) {
      return 'S级';
    } else if (percentage >= 80) {
      return 'A级';
    } else if (percentage >= 60) {
      return 'B级';
    } else {
      return 'C级';
    }
  };

  // 转换数据格式以适配 RadarChart，使用比例值显示在图表上
  const chartData = totalInfo.map((item) => ({
    subject: item.label,
    score: (item.score / item.total) * 100, // 转换为百分比用于图表显示
    actualScore: item.score, // 保留原始分数用于tooltip
    total: item.total,
    grade: getGrade(item.score, item.total), // 计算等级
    fullMark: 100 // 固定满分为100
  }));

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* 雷达图 */}
      <div className="w-[90vw] h-64">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart cx="50%" cy="50%" outerRadius="60%" data={chartData}>
            <PolarGrid stroke="#E5E7EB" strokeOpacity={0.5} />
            <PolarAngleAxis
              dataKey="subject"
              tick={({ payload, x, y, textAnchor, index }) => {
                const text = payload?.value || '';
                const displayText = text.length > 6 ? text.substring(0, 6) + '...' : text;
                const dataPoint = chartData[index];
                const scoreText = `${dataPoint.actualScore}分（${dataPoint.grade}）`;
                const yPos = typeof y === 'number' ? y : 0;

                return (
                  <g>
                    <text
                      x={x}
                      y={yPos}
                      textAnchor={textAnchor}
                      fontSize={12}
                      fill="#606266"
                      className="text-xs"
                    >
                      {displayText}
                    </text>
                    <text
                      x={x}
                      y={yPos + 15}
                      textAnchor={textAnchor}
                      fontSize={10}
                      fill="#86909c"
                      className="text-xs"
                    >
                      {scoreText}
                    </text>
                  </g>
                );
              }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={false}
              tickCount={5}
            />
            <Radar
              name={t('ai.analysis.scoreLabel', '得分')}
              dataKey="score"
              stroke="#A782FF"
              fill="#A782FF"
              fillOpacity={0.3}
              strokeWidth={2}
              dot={{ fill: '#A782FF', strokeWidth: 2, r: 4 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(0,0,0,0.8)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff'
              }}
              formatter={(_value: number, _name: string, props: any) => {
                 const actualScore = props.payload?.actualScore || 0;
                 const total = props.payload?.total || 100;
                 const grade = props.payload?.grade || '';
                 return [
                   `${actualScore}/${total}${t('common.score', '分')} (${grade})`,
                   t('ai.analysis.scoreLabel', '得分')
                 ];
               }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ScoreRadar;